import { useState, useCallback, useEffect, useRef } from 'react';
import { getToken } from 'firebase/messaging';
import { getFirebaseMessaging } from '@/core/firebase/config';
import useSaveNotificationToken from './useSaveNotificationToken';

type PermissionState = NotificationPermission | 'unsupported';

interface UseNotificationPermissionOptions {
  userId: string | null | undefined;
}

/**
 * Hook to manage push notification permission and FCM token lifecycle.
 *
 * - Checks current browser permission state
 * - Provides a function to request permission
 * - Automatically saves/refreshes the FCM token when permission is granted
 * - Registers the Firebase messaging service worker for push handling
 */
export default function useNotificationPermission({ userId }: UseNotificationPermissionOptions) {
  const [permission, setPermission] = useState<PermissionState>(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported';
    }
    return Notification.permission;
  });

  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const previousUserIdRef = useRef<string | null>(userId ?? null);
  const { saveToken, removeToken } = useSaveNotificationToken();

  /**
   * Register the Firebase messaging service worker.
   * This SW handles background push notifications separately from the Workbox SW.
   */
  const getSwRegistration = useCallback(async () => {
    if (swRegistrationRef.current) return swRegistrationRef.current;

    if (!('serviceWorker' in navigator)) return null;

    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/firebase-cloud-messaging-push-scope',
      });
      swRegistrationRef.current = registration;
      return registration;
    } catch (err) {
      console.error('Failed to register Firebase messaging SW:', err);
      return null;
    }
  }, []);

  const getCurrentToken = useCallback(async () => {
    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    const swRegistration = await getSwRegistration();
    if (!swRegistration) return null;

    try {
      return await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_MESSAGING_VAPID_KEY,
        serviceWorkerRegistration: swRegistration,
      });
    } catch (err) {
      console.error('Failed to get FCM token:', err);
      return null;
    }
  }, [getSwRegistration]);

  /**
   * Retrieve and save the FCM token (called when permission is already granted
   * or right after the user grants it).
   */
  const refreshToken = useCallback(async () => {
    if (!userId) return;
    const token = await getCurrentToken();
    if (!token) return;
    await saveToken(userId, token);
  }, [userId, saveToken, getCurrentToken]);

  /**
   * Request notification permission from the browser. If granted,
   * immediately retrieves and saves the FCM token.
   */
  const requestPermission = useCallback(async () => {
    if (permission === 'unsupported') return;

    const result = await Notification.requestPermission();
    setPermission(result);
    // Token save is handled by the useEffect that reacts to permission becoming 'granted'
  }, [permission]);

  // On mount (and when userId changes), if permission is already granted,
  // silently refresh the token (tokens can rotate).
  useEffect(() => {
    if (permission === 'granted' && userId) {
      refreshToken();
    }
  }, [permission, userId, refreshToken]);

  // If account changes while using the same browser token, remove the stale
  // token association from the previous user to avoid cross-account delivery.
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId ?? null;

    if (permission !== 'granted' || !previousUserId || !userId || previousUserId === userId) {
      return;
    }

    void (async () => {
      const token = await getCurrentToken();
      if (!token) return;
      await removeToken(previousUserId, token);
    })();
  }, [permission, userId, getCurrentToken, removeToken]);

  // Remove this browser token on logout/unmount so old sessions do not keep
  // receiving notifications on shared devices.
  useEffect(() => {
    return () => {
      const currentUserId = previousUserIdRef.current;
      if (permission !== 'granted' || !currentUserId) return;

      void (async () => {
        const token = await getCurrentToken();
        if (!token) return;
        await removeToken(currentUserId, token);
      })();
    };
  }, [permission, getCurrentToken, removeToken]);

  return {
    /** Current permission state: 'default' | 'granted' | 'denied' | 'unsupported' */
    permission,
    /** Request browser notification permission */
    requestPermission,
    /** Whether the user has never been asked */
    canAsk: permission === 'default',
    /** Whether notifications are active */
    isEnabled: permission === 'granted',
  };
}
